import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const FilterPanel = ({ 
  filters, 
  onFilterChange, 
  onApplyFilters, 
  onResetFilters,
  resultCount = 0,
  isLoading = false 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const relationshipStatusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'single', label: 'Single' },
    { value: 'married', label: 'Married' },
    { value: 'divorced', label: 'Divorced' },
    { value: 'separated', label: 'Separated' },
    { value: 'complicated', label: 'It\'s Complicated' }
  ];

  const sortOptions = [
    { value: 'relevance', label: 'Most Relevant' },
    { value: 'active', label: 'Recently Active' },
    { value: 'newest', label: 'Newest Members' },
    { value: 'compatibility', label: 'Best Match' }
  ];

  const verificationOptions = [
    { value: 'all', label: 'All Users' },
    { value: 'verified', label: 'Verified Only' },
    { value: 'photo_verified', label: 'Photo Verified' }
  ];

  const activityLevelOptions = [
    { value: 'all', label: 'All Activity' },
    { value: 'very_active', label: 'Very Active' },
    { value: 'active', label: 'Active' },
    { value: 'moderate', label: 'Moderate' }
  ];

  const interestsList = [
    'Travel', 'Music', 'Fitness', 'Movies', 'Reading', 
    'Cooking', 'Sports', 'Art', 'Photography', 'Dancing',
    'Gaming', 'Yoga', 'Fashion', 'Technology', 'Nature'
  ];

  const handleLocationChange = (e) => {
    onFilterChange({ ...filters, locationRadius: parseInt(e?.target?.value) });
  };

  const handleAgeChange = (type, value) => {
    onFilterChange({ 
      ...filters, 
      [type]: parseInt(value) 
    });
  };

  const handleInterestToggle = (interest) => {
    const currentInterests = filters?.interests || [];
    const newInterests = currentInterests?.includes(interest)
      ? currentInterests?.filter(i => i !== interest)
      : [...currentInterests, interest];
    onFilterChange({ ...filters, interests: newInterests });
  };

  return (
    <div className="bg-card rounded-xl shadow-warm p-4 md:p-6 space-y-4 md:space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="SlidersHorizontal" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-base md:text-lg font-heading font-semibold">Search Filters</h2>
            <p className="text-xs md:text-sm text-muted-foreground">
              {isLoading ? 'Searching...' : `${resultCount} matches found`}
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="md:hidden p-2 hover:bg-muted rounded-lg transition-smooth"
        >
          <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={20} />
        </button>
      </div>
      <div className={`space-y-4 md:space-y-6 ${isExpanded ? 'block' : 'hidden md:block'}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-caption font-medium">
              <Icon name="MapPin" size={16} color="var(--color-primary)" />
              Location Radius: {filters?.locationRadius}km
            </label>
            <input
              type="range"
              min="5"
              max="100"
              step="5"
              value={filters?.locationRadius}
              onChange={handleLocationChange}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>5km</span>
              <span>100km</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-caption font-medium">
              <Icon name="Calendar" size={16} color="var(--color-primary)" />
              Age Range
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="18"
                max="100"
                value={filters?.ageMin}
                onChange={(e) => handleAgeChange('ageMin', e?.target?.value)}
                className="flex-1"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="number"
                min="18"
                max="100"
                value={filters?.ageMax}
                onChange={(e) => handleAgeChange('ageMax', e?.target?.value)}
                className="flex-1"
              />
            </div>
          </div>

          <Select
            label="Relationship Status"
            options={relationshipStatusOptions}
            value={filters?.relationshipStatus}
            onChange={(value) => onFilterChange({ ...filters, relationshipStatus: value })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Select
            label="Sort By"
            options={sortOptions}
            value={filters?.sortBy}
            onChange={(value) => onFilterChange({ ...filters, sortBy: value })}
          />

          <Select
            label="Verification Status"
            options={verificationOptions}
            value={filters?.verification}
            onChange={(value) => onFilterChange({ ...filters, verification: value })}
          />

          <Select
            label="Activity Level"
            options={activityLevelOptions}
            value={filters?.activityLevel}
            onChange={(value) => onFilterChange({ ...filters, activityLevel: value })}
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              label="Show only online users"
              checked={filters?.onlineOnly}
              onChange={(e) => onFilterChange({ ...filters, onlineOnly: e?.target?.checked })}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              label="Show users with photos only"
              checked={filters?.withPhotos}
              onChange={(e) => onFilterChange({ ...filters, withPhotos: e?.target?.checked })}
            />
          </div>
        </div>

        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm font-caption font-medium">
            <Icon name="Heart" size={16} color="var(--color-primary)" />
            Interests
          </label>
          <div className="flex flex-wrap gap-2">
            {interestsList?.map((interest) => (
              <button
                key={interest}
                onClick={() => handleInterestToggle(interest)}
                className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-smooth ${
                  (filters?.interests || [])?.includes(interest)
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                {interest}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button
            variant="outline"
            fullWidth
            iconName="RotateCcw"
            iconPosition="left"
            onClick={onResetFilters}
          >
            Reset Filters
          </Button>
          <Button
            variant="default"
            fullWidth
            iconName="Search"
            iconPosition="left"
            onClick={onApplyFilters}
            loading={isLoading}
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;